/*
  ReferralSourcesReport — "Where clients are hearing about us."
  Bucketed count of new clients grouped by client.referral.source
  from the shared canonical registry.
*/
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Compass, Loader2, RefreshCcw, Megaphone, Users, AlertCircle,
} from "lucide-react";

const PERIODS = [
  { value: "last_3m", label: "Last 3 months" },
  { value: "last_6m", label: "Last 6 months" },
  { value: "last_12m", label: "Last 12 months" },
  { value: "ytd", label: "Year to date" },
  { value: "all", label: "All time" },
];

function pctString(share) {
  return `${(share * 100).toFixed(1)}%`;
}

// Compact horizontal bar. Width scales to the largest bucket in the
// list so relative comparison stays honest even when the top bucket
// is small.
function BucketBar({ bucket, maxCount }) {
  const width = maxCount > 0 ? (bucket.count / maxCount) * 100 : 0;
  const paid = bucket.is_paid;
  return (
    <div className="grid grid-cols-[minmax(220px,1fr)_minmax(140px,220px)_60px_60px] items-center gap-2 py-1.5"
         data-testid={`referral-bucket-${bucket.channel}`}>
      <div className="flex items-center gap-2">
        <span className="text-sm text-zinc-900 truncate">{bucket.label}</span>
        {paid && (
          <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-2xs uppercase">
            Paid
          </Badge>
        )}
        {bucket.channel === "__unanswered__" && (
          <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200 text-2xs uppercase">
            Blank
          </Badge>
        )}
      </div>
      <div className="h-2 rounded-full bg-zinc-100 relative overflow-hidden">
        <div
          className={paid ? "absolute inset-y-0 left-0 bg-amber-400" : "absolute inset-y-0 left-0 bg-indigo-500"}
          style={{ width: `${width}%` }}
        />
      </div>
      <div className="text-sm tabular-nums text-zinc-900 text-right">{bucket.count}</div>
      <div className="text-xs tabular-nums text-zinc-500 text-right">{pctString(bucket.share)}</div>
    </div>
  );
}

export default function ReferralSourcesReport() {
  const [period, setPeriod] = useState("last_6m");
  const [includeUnanswered, setIncludeUnanswered] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/reports/referral-sources", {
        params: { period, include_unanswered: includeUnanswered },
      });
      setData(r.data);
    } catch (e) {
      toast.error("Couldn't load report", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, includeUnanswered]);

  const maxCount = data?.buckets?.reduce((m, b) => Math.max(m, b.count), 0) || 0;

  return (
    <div className="space-y-6" data-testid="referral-sources-report">
      {/* Header */}
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800">
              <Compass className="h-4 w-4" />
            </div>
            <div>
              <div className="font-medium text-sm">Where clients are hearing about us</div>
              <div className="text-xs text-zinc-500">
                Grouped by <code className="px-1 py-0.5 bg-zinc-100 rounded">client.referral.source</code>{" "}
                from the shared canonical registry.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="h-8 text-xs rounded-md border border-zinc-300 bg-white px-2"
              data-testid="referral-period-select"
            >
              {PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={includeUnanswered}
                onChange={(e) => setIncludeUnanswered(e.target.checked)}
                className="h-3.5 w-3.5 accent-indigo-600"
                data-testid="referral-include-unanswered"
              />
              Include blanks
            </label>
            <Button
              size="sm" variant="outline"
              onClick={load}
              disabled={loading}
              data-testid="referral-refresh-btn"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                        : <RefreshCcw className="h-3.5 w-3.5 mr-1" />}
              Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* KPI tiles */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">New clients ({data.period.label})</div>
            <div className="text-2xl font-medium mt-1 tabular-nums" data-testid="referral-kpi-total">
              {data.total_new_clients}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              <Users className="inline h-3 w-3 mr-1" />
              {data.total_answered} answered ({pctString(data.answered_rate || 0)})
            </div>
          </Card>
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">From paid channels</div>
            <div className="text-2xl font-medium mt-1 tabular-nums text-amber-700"
                 data-testid="referral-kpi-paid">
              {data.paid_channel_summary?.total_from_paid || 0}
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              <Megaphone className="inline h-3 w-3 mr-1" />
              Google · Facebook · Instagram
            </div>
          </Card>
          <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
            <div className="text-2xs uppercase tracking-wider text-zinc-500">Recalled seeing an ad</div>
            <div className="text-2xl font-medium mt-1 tabular-nums"
                 data-testid="referral-kpi-ad-recall">
              {data.paid_channel_summary?.recalled_ad || 0}
              <span className="text-xs text-zinc-500 ml-2 font-normal">
                of {data.paid_channel_summary?.total_from_paid || 0}
              </span>
            </div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {(data.paid_channel_summary?.did_not_recall || 0)} said no ·{" "}
              {(data.paid_channel_summary?.unsure || 0)} unsure
            </div>
          </Card>
        </div>
      )}

      {/* Channel breakdown */}
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
        <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2">Channel breakdown</div>
        {loading ? (
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        ) : !data?.buckets?.length ? (
          <div className="border border-dashed border-zinc-300 rounded-md p-6 text-center text-sm text-zinc-500 flex flex-col items-center gap-2">
            <AlertCircle className="h-5 w-5 text-zinc-400" />
            No referral data in this period yet.
            <span className="text-xs">Onboarding submissions collect <code className="px-1 py-0.5 bg-zinc-100 rounded">client.referral.source</code> — check back after your next few intakes.</span>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {data.buckets.map((b) => (
              <BucketBar key={b.channel} bucket={b} maxCount={maxCount} />
            ))}
          </div>
        )}
      </Card>

      {/* Paid-channel ad-recall detail */}
      {data?.buckets?.some((b) => b.is_paid) && (
        <Card className="p-4 rounded-lg border border-amber-200 bg-amber-50/40 shadow-none">
          <div className="text-2xs uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1">
            <Megaphone className="h-3.5 w-3.5" /> Paid-channel ad recall detail
          </div>
          <div className="text-xs text-zinc-600 mb-2">
            Google's analytics can only tell you clicks — the numbers below tell you what clients CONSCIOUSLY remember.
            A large "no" or "unsure" column against an expensive ad spend usually means the ad is under-performing on brand recall.
          </div>
          <div className="grid grid-cols-4 gap-2 text-xs text-zinc-700 font-medium border-b border-amber-200 pb-1">
            <div>Channel</div>
            <div className="text-right">Recalled ad</div>
            <div className="text-right">Didn't recall</div>
            <div className="text-right">Unsure</div>
          </div>
          {data.buckets.filter((b) => b.is_paid).map((b) => (
            <div key={b.channel} className="grid grid-cols-4 gap-2 text-xs py-1"
                 data-testid={`referral-paid-detail-${b.channel}`}>
              <div>{b.label}</div>
              <div className="text-right tabular-nums text-emerald-700 font-medium">
                {b.ad_recall_breakdown?.yes_ad || 0}
              </div>
              <div className="text-right tabular-nums text-rose-700">
                {b.ad_recall_breakdown?.no_ad || 0}
              </div>
              <div className="text-right tabular-nums text-zinc-500">
                {b.ad_recall_breakdown?.unsure_ad || 0}
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Top referrers */}
      {data?.top_referrers?.length > 0 && (
        <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
          <div className="text-2xs uppercase tracking-wider text-zinc-500 mb-2">Top referrers</div>
          <div className="text-xs text-zinc-500 mb-2">
            Names that appear on 2+ intakes — thank-you cards and case-managers to nurture.
          </div>
          <div className="divide-y divide-zinc-100">
            {data.top_referrers.map((r, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 text-sm"
                   data-testid={`referral-top-referrer-${i}`}>
                <div>
                  <span className="font-medium">{r.name}</span>{" "}
                  <span className="text-xs text-zinc-500">· {r.channel_label}</span>
                </div>
                <div className="tabular-nums font-medium">{r.count} referrals</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
